import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import SEO from '../components/SEO';
import ShareButtons from '../components/ShareButtons';
import AdCard from '../components/AdCard';
import { api } from '../api';
import { useTheme } from '../context/ThemeContext';
import { fetchActiveAds, pickWeightedAd } from '../lib/ads';

function ArticleView() {
  const { slug } = useParams();
  const { darkMode } = useTheme();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ads, setAds] = useState([]);

  useEffect(() => {
    api(`/api/articles/${slug}`).then(res => {
      if (res.ok) return res.json();
      return null;
    }).then(data => {
      setArticle(data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    fetchActiveAds().then(setAds);
  }, []);

  // Two ad slots per article page:
  //   - midAd: placed between sections inside the article body. A split is
  //     only valid when the block AFTER the split is a markdown heading,
  //     so the ad always sits at a genuine section break (never mid-
  //     section between two related paragraphs, which readers perceive as
  //     mid-paragraph).
  //   - bottomAd: placed under the share buttons. Different from midAd
  //     when at least two ads are active; otherwise falls back to the
  //     single ad.
  const { midAd, contentTop, contentBottom, bottomAd } = useMemo(() => {
    const full = article?.content || '';
    if (!ads.length) {
      return { midAd: null, contentTop: full, contentBottom: '', bottomAd: null };
    }

    const isHeading = (p) => /^#{1,6}\s/.test((p || '').trimStart());

    let chosenMid = null;
    let top = full;
    let bottom = '';

    if (full) {
      const paragraphs = full.split(/\n\n+/);
      if (paragraphs.length >= 4) {
        // Widen to 20-80% — more of the article is eligible, while
        // still keeping the ad safely away from the first / last block.
        const minIdx = Math.max(2, Math.floor(paragraphs.length * 0.2));
        const maxIdx = Math.max(minIdx, Math.floor(paragraphs.length * 0.8));
        const sectionBreaks = [];
        for (let i = minIdx; i <= maxIdx; i++) {
          if (isHeading(paragraphs[i])) sectionBreaks.push(i);
        }
        if (sectionBreaks.length > 0) {
          const candidate = pickWeightedAd(ads);
          if (candidate) {
            const splitIdx = sectionBreaks[Math.floor(Math.random() * sectionBreaks.length)];
            chosenMid = candidate;
            top = paragraphs.slice(0, splitIdx).join('\n\n');
            bottom = paragraphs.slice(splitIdx).join('\n\n');
          }
        }
        // If no section break exists in the middle of the article we
        // simply don't show a mid-article ad. The bottom ad still runs.
      }
    }

    // Bottom ad — always try to show one. Prefer a different ad than the
    // mid slot when possible.
    const pool = chosenMid ? ads.filter(a => a.id !== chosenMid.id) : ads;
    const chosenBottom = pool.length > 0 ? pickWeightedAd(pool) : null;

    return { midAd: chosenMid, contentTop: top, contentBottom: bottom, bottomAd: chosenBottom };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ads, article?.id]);

  const osloDay = (dateStr) => {
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString('sv-SE', { timeZone: 'Europe/Oslo' });
  };

  const showUpdated = (created, updated) => {
    if (!updated || !created) return false;
    const c = new Date(created).getTime();
    const u = new Date(updated).getTime();
    if (Number.isNaN(c) || Number.isNaN(u)) return false;
    if (osloDay(created) === osloDay(updated)) return false;
    return Math.abs(u - c) > 36 * 60 * 60 * 1000;
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('nb-NO', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return <div className="min-h-screen pt-24 px-4 text-center"><p className="text-gray-400 text-lg">Laster...</p></div>;
  }

  if (!article) {
    return (
      <div className="min-h-screen pt-24 px-4 text-center">
        <SEO title="Artikkel ikke funnet" noindex />
        <h1 className="text-3xl font-bold text-white mb-4">Artikkel ikke funnet</h1>
        <Link to="/artikler" className="text-teal-400 hover:text-teal-300 underline">
          Tilbake til artikler
        </Link>
      </div>
    );
  }

  const bg = darkMode ? 'bg-slate-900' : 'bg-stone-50';
  const textColor = darkMode ? 'text-gray-200' : 'text-stone-800';
  const headingColor = darkMode ? 'text-white' : 'text-stone-900';
  const metaColor = darkMode ? 'text-gray-500' : 'text-stone-500';
  const tagBg = darkMode ? 'bg-teal-500 bg-opacity-15 text-teal-400' : 'bg-teal-100 text-teal-700';
  const borderColor = darkMode ? 'border-slate-800' : 'border-stone-200';
  const cardBg = darkMode ? 'bg-slate-800 bg-opacity-60' : 'bg-white';
  const blockquoteBorder = darkMode ? 'border-teal-500' : 'border-teal-600';
  const blockquoteBg = darkMode ? 'bg-slate-800' : 'bg-stone-100';
  const codeBg = darkMode ? 'bg-slate-800' : 'bg-stone-100';
  const codeText = darkMode ? 'text-teal-300' : 'text-teal-700';
  const linkColor = darkMode ? 'text-teal-400 hover:text-teal-300' : 'text-teal-600 hover:text-teal-700';
  const hrColor = darkMode ? 'border-slate-700' : 'border-stone-300';
  const backLinkColor = darkMode ? 'text-gray-400 hover:text-teal-400' : 'text-stone-500 hover:text-teal-600';

  return (
    <div className={`min-h-screen transition-colors duration-300 ${bg}`}>
      <SEO
        title={article.title}
        path={`/artikler/${article.slug}`}
        description={article.excerpt}
        type="article"
        image={article.coverImage || undefined}
        article={article}
      />
      <div className="max-w-3xl mx-auto px-4 pt-20 sm:pt-24 pb-16">
        {/* Top bar */}
        <div className="flex items-center justify-between mb-8">
          <Link
            to="/artikler"
            className={`flex items-center gap-2 text-sm font-medium transition-colors ${backLinkColor}`}
            style={{ fontFamily: "'Lora', serif" }}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Tilbake til artikler
          </Link>
        </div>

        {/* Article header */}
        <header className="mb-10">
          <div className="flex flex-wrap gap-2 mb-4">
            {article.tags?.map(tag => (
              <Link key={tag} to={`/merke/${encodeURIComponent(tag)}`} className={`px-3 py-1 text-xs rounded-full font-medium transition-opacity hover:opacity-80 ${tagBg}`}>
                {tag}
              </Link>
            ))}
          </div>
          <h1
            className={`text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-4 ${headingColor}`}
            style={{ fontFamily: "'Lora', serif" }}
          >
            {article.title}
          </h1>
          {article.excerpt && (
            <p
              className={`text-lg sm:text-xl leading-relaxed mb-5 font-medium ${darkMode ? 'text-gray-300' : 'text-stone-600'}`}
              style={{ fontFamily: "'Lora', serif" }}
            >
              {article.excerpt}
            </p>
          )}
          <div className={`flex items-center gap-3 text-sm ${metaColor}`} style={{ fontFamily: "'Lora', serif" }}>
            <span className="relative group cursor-default">
              {article.author}
              <span className={`absolute left-0 top-full mt-2 w-64 p-3 rounded-lg text-xs leading-relaxed opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10 shadow-lg ${darkMode ? 'bg-slate-800 text-gray-300 border border-slate-700' : 'bg-white text-stone-600 border border-stone-200'}`}>
                Artikkelen kan i noen tilfeller være delvis generert basert på manus fra videoer. Det er ikke hentet informasjon utenfra, og alt innhold er kvalitetsikret.
              </span>
            </span>
            <span>·</span>
            <span>{formatDate(article.createdAt)}</span>
            {showUpdated(article.createdAt, article.updatedAt) && (
              <>
                <span>·</span>
                <span>Oppdatert {formatDate(article.updatedAt)}</span>
              </>
            )}
          </div>

          {/* Share buttons */}
          <div className="mt-4">
            <ShareButtons
              url={`https://effektnorge.no/artikler/${article.slug}`}
              title={article.title}
              darkMode={darkMode}
            />
          </div>
        </header>

        {/* Small YouTube link at top */}
        {article.youtubeUrl && !article.hideYoutube && (
          <a
            href={article.youtubeUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={`inline-flex items-center gap-2 text-sm font-medium mb-6 transition-colors ${darkMode ? 'text-gray-400 hover:text-white' : 'text-stone-500 hover:text-stone-800'}`}
            style={{ fontFamily: "'Lora', serif" }}
          >
            <svg className="w-4 h-4 text-red-500 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
              <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814Z"/>
              <path fill="#fff" d="m9.545 15.568 6.273-3.568-6.273-3.568v7.136Z"/>
            </svg>
            Se videoen på YouTube
          </a>
        )}

        {/* Cover image (LCP candidate on article pages) */}
        {article.coverImage && (
          <img
            src={article.coverImage}
            alt={article.title}
            className="w-full rounded-2xl mb-10 shadow-lg"
            loading="eager"
            fetchpriority="high"
            decoding="async"
          />
        )}

        {/* Article content */}
        <article
          className={`article-content ${textColor}`}
          style={{ fontFamily: "'Lora', serif" }}
        >
          {(() => {
            const mdComponents = {
              // The article title is already an <h1> above this content,
              // so markdown headings are downgraded by one level to keep a
              // single <h1> per page (SEO + a11y best practice).
              h1: ({ children }) => (
                <h2 className={`text-3xl sm:text-4xl font-bold mt-10 mb-4 ${headingColor}`}>{children}</h2>
              ),
              h2: ({ children }) => (
                <h3 className={`text-2xl sm:text-3xl font-bold mt-8 mb-3 ${headingColor}`}>{children}</h3>
              ),
              h3: ({ children }) => (
                <h4 className={`text-xl sm:text-2xl font-semibold mt-6 mb-2 ${headingColor}`}>{children}</h4>
              ),
              p: ({ children }) => (
                <p className="mb-5 leading-relaxed text-lg">{children}</p>
              ),
              ul: ({ children }) => (
                <ul className="mb-5 ml-6 space-y-2 list-disc">{children}</ul>
              ),
              ol: ({ children }) => (
                <ol className="mb-5 ml-6 space-y-2 list-decimal">{children}</ol>
              ),
              li: ({ children }) => (
                <li className="text-lg leading-relaxed">{children}</li>
              ),
              blockquote: ({ children }) => (
                <blockquote className={`border-l-4 ${blockquoteBorder} ${blockquoteBg} rounded-r-xl pl-5 pr-4 py-4 my-6 italic`}>
                  {children}
                </blockquote>
              ),
              code: ({ className, children, node, ...props }) => {
                const isBlock = node?.position?.start?.line !== node?.position?.end?.line || className;
                if (!isBlock) {
                  return (
                    <code className={`${codeBg} ${codeText} px-1.5 py-0.5 rounded text-sm font-mono`}>
                      {children}
                    </code>
                  );
                }
                return (
                  <code className={`${codeText} text-sm font-mono`} {...props}>{children}</code>
                );
              },
              pre: ({ children }) => (
                <pre className={`${codeBg} rounded-xl p-5 my-6 overflow-x-auto`}>
                  {children}
                </pre>
              ),
              a: ({ href, children }) => (
                <a href={href} className={`underline underline-offset-2 transition-colors ${linkColor}`} target="_blank" rel="noopener noreferrer">
                  {children}
                </a>
              ),
              img: ({ src, alt }) => (
                <img src={src} alt={alt || ''} loading="lazy" decoding="async" className="w-full rounded-xl my-6 shadow-md" />
              ),
              hr: () => (
                <hr className={`my-8 ${hrColor}`} />
              ),
              strong: ({ children }) => (
                <strong className={`font-bold ${headingColor}`}>{children}</strong>
              ),
              table: ({ children }) => (
                <div className="overflow-x-auto my-6 -mx-4 px-4 sm:mx-0 sm:px-0">
                  <table className={`w-full border-collapse border ${borderColor} rounded-xl text-sm sm:text-base min-w-[400px]`}>{children}</table>
                </div>
              ),
              th: ({ children }) => (
                <th className={`${cardBg} ${headingColor} px-3 py-2 sm:px-4 sm:py-3 text-left font-semibold border ${borderColor} text-xs sm:text-sm whitespace-nowrap`}>{children}</th>
              ),
              td: ({ children }) => (
                <td className={`px-3 py-2 sm:px-4 sm:py-3 border ${borderColor} text-sm`}>{children}</td>
              ),
            };
            if (midAd && contentBottom) {
              return (
                <>
                  <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
                    {contentTop}
                  </ReactMarkdown>
                  <AdCard ad={midAd} variant="banner" />
                  <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
                    {contentBottom}
                  </ReactMarkdown>
                </>
              );
            }
            return (
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
                {article.content}
              </ReactMarkdown>
            );
          })()}
        </article>

        {/* YouTube video link - large */}
        {article.youtubeUrl && !article.hideYoutube && (
          <div className={`mt-10 p-6 sm:p-8 rounded-2xl border ${borderColor} ${cardBg}`}>
            <a
              href={article.youtubeUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 group"
              style={{ fontFamily: "'Lora', serif" }}
            >
              <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-red-500 transition-colors">
                <svg className="w-6 h-6 text-white ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z"/>
                </svg>
              </div>
              <div>
                <span className={`text-lg font-bold block transition-colors ${darkMode ? 'text-white group-hover:text-teal-400' : 'text-stone-900 group-hover:text-teal-600'}`}>
                  Se videoen på YouTube
                </span>
                <span className={`text-sm ${metaColor}`}>Hele videoen som artikkelen er basert på</span>
              </div>
            </a>
          </div>
        )}

        {/* Bottom share */}
        <div className={`mt-10 pt-6 border-t ${borderColor}`}>
          <p className={`text-sm font-medium mb-3 ${metaColor}`} style={{ fontFamily: "'Lora', serif" }}>Del artikkelen</p>
          <ShareButtons
            url={`https://effektnorge.no/artikler/${article.slug}`}
            title={article.title}
            darkMode={darkMode}
          />
        </div>

        {/* Bottom ad — renders after the YouTube CTA and share buttons,
            before the "Alle artikler" back-navigation link. */}
        {bottomAd && (
          <AdCard ad={bottomAd} variant="banner" />
        )}

        {/* Bottom navigation */}
        <div className={`mt-8 pt-8 border-t ${borderColor}`}>
          <Link
            to="/artikler"
            className={`inline-flex items-center gap-2 text-sm font-medium transition-colors ${backLinkColor}`}
            style={{ fontFamily: "'Lora', serif" }}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Alle artikler
          </Link>
        </div>
      </div>
    </div>
  );
}

export default ArticleView;
